import React from 'react';
import { Box, Flex, Link, Icon, Tooltip } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { KTag } from '@/ui-library/components/atoms/KTag';
import dayjs from 'dayjs';
import { useDateFormat } from '@/hooks/useDateFormat';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { FiExternalLink } from 'react-icons/fi';
import { secondsToHumanTime } from '@/utils/secondsToHumanTime';
import { KButton } from '@/ui-library/components/atoms/KButton';
import { Eye } from '@kula-ai/icons/dual-tone/Eye';
import { Copy03 } from '@kula-ai/icons/dual-tone/Copy03';
import { Download02 } from '@kula-ai/icons/solid/Download02';
import { useMutation } from '@tanstack/react-query';
import { ASSESSMENT_API } from '../../api/codingAssessment.api';
import { useShowToast } from '@/hooks/useShowToast';
import { Check } from '@kula-ai/icons/solid/Check';
import { useState } from 'react';
import { selectSettings } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { PreviewPdfModal } from '@/components/ats/FileUpload/PreviewPdfModal';
import { PlatformLogical } from '@/types/ext_assessment';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { MOBILE_BREAKPOINT } from '@/constants/mobile-breakpoints';

const CODEINTERVIEW_RESULT_BASE_URL =
  'https://app.remoteinterview.io/tests/#?roomid=';

const PLATFORMS = {
  HACKERRANK: 'hackerrank',
  HACKEREARTH: 'hackerearth',
  CRITERIACORP: 'criteriacorp',
  HIREFLIX: 'hireflix',
  CODEINTERVIEW: 'codeinterview',
};

const PLATFORM_CONFIG = {
  hackerrank: {
    name: 'HackerRank',
    connectionKey: 'connected_hackerrank',
    reconnectMessage:
      'HackerRank is no longer connected. Reconnect and try again.',
  },
  hackerearth: {
    name: 'HackerEarth',
    connectionKey: 'connected_hackerearth',
    reconnectMessage:
      'HackerEarth is no longer connected. Reconnect and try again.',
  },
  criteriacorp: {
    name: 'Criteria Corp',
    connectionKey: 'connected_criteriacorp',
    reconnectMessage:
      'Criteria Corp is no longer connected. Reconnect and try again.',
  },
  hireflix: {
    name: 'Hireflix',
    connectionKey: 'connected_hireflix',
    reconnectMessage:
      'Hireflix is no longer connected. Reconnect and try again.',
  },
  codeinterview: {
    name: 'CodeInterview',
    connectionKey: 'connected_codeinterview',
    reconnectMessage:
      'CodeInterview is no longer connected. Reconnect and try again.',
  },
};

const calculateDurationInSeconds = (
  startTime: string,
  endTime: string
): number => {
  const start = dayjs(startTime);
  const end = dayjs(endTime);
  return end.diff(start, 'second');
};

interface AssessmentData {
  test_name?: string;
  test_link?: string;
  completed_at?: string;
  started_at?: string;
  duration?: string;
  plagiarism_status?: boolean;
  integrity_score?: string;
  report_url?: string;
  pdf_url?: string;
  score?: number;
  max_score?: number;
  percentage_score?: number;
  status?: any;
  percentile?: number;
  raw_score?: number;
  attempt_starttime?: string;
  attempt_endtime?: string;
  hireflix_url_private?: string;
}

interface AssessmentDetailsProps {
  assessmentDetails?: any;
  platform: PlatformLogical | 'internal' | string;
}

export const AssessmentDetails: React.FC<AssessmentDetailsProps> = ({
  assessmentDetails,
  platform,
}) => {
  const [copied, setCopied] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const toast = useShowToast();
  const settingsDetails = useSelector(selectSettings);
  const platformConfig = PLATFORM_CONFIG[platform];
  const isPlatformEnabled = platformConfig
    ? settingsDetails?.account?.[platformConfig.connectionKey]
    : platform === 'internal'
      ? true
      : false;
  const isMobile = useIsMobile({ mobileBreakpoint: MOBILE_BREAKPOINT });

  const generateReportMutation = useMutation({
    mutationFn: (assessmentId: string) =>
      ASSESSMENT_API.generateReport(assessmentId, platform as PlatformLogical),
    onSuccess: (response) => {
      if (response?.data?.download_url) {
        window.open(response.data.download_url, '_self');
      } else {
        toast({ title: 'Report generated successfully', status: 'success' });
      }
    },
    onError: (error) => {
      toast({
        title: 'Failed to generate report',
        status: 'error',
        description: error?.message || 'Please try again later',
      });
    },
  });

  const assessmentData: AssessmentData = assessmentDetails?.submission || {
    name: 'Senior Software Engineer test',
    submissionDate: '22 June 24, 9:30AM',
    startTime: '8:30AM',
    endTime: '9:30AM',
    duration: '1 hour',
    score: '75/100',
    plagiarism: false,
    reportUrl: 'https://example.com/report',
  };

  const {
    test_name,
    completed_at,
    started_at,
    duration: originalDuration,
    plagiarism_status,
    integrity_score,
    report_url,
    pdf_url,
    hireflix_url_private,
    score,
    max_score,
    percentile,
    raw_score,
  } = assessmentData;

  const { dateFormat, timeFormat } = useDateFormat();

  const calculatedDuration =
    started_at && completed_at
      ? secondsToHumanTime(calculateDurationInSeconds(started_at, completed_at))
      : originalDuration;

  const handleView = async () => {
    if (pdf_url) {
      if (platform === PLATFORMS.HACKEREARTH) {
        try {
          const response = await fetch(pdf_url);
          const blob = await response.blob();
          // We use Blob URLs here because the url that we get from hackerearth is triggering the pdf viewer to download the file instead of displaying it in the modal.
          // so that we use blob url to display the pdf in the modal.
          const blobUrl = URL.createObjectURL(blob);
          setPdfBlobUrl(blobUrl);
          setIsPdfModalOpen(true);
        } catch {
          toast({
            title:
              'Unable to preview the PDF. Please try downloading it instead.',
            status: 'error',
          });
        }
      } else if (platform === PLATFORMS.CRITERIACORP) {
        setIsPdfModalOpen(true);
      } else {
        window.open(pdf_url, '_blank');
      }
    }
  };

  const handleCopy = () => {
    if (report_url) {
      navigator.clipboard.writeText(report_url);
      setCopied(true);
      toast({ title: 'Link copied to clipboard', status: 'success' });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = async () => {
    const assessmentId = assessmentDetails?.id;

    if (!assessmentId) {
      toast({ title: 'Assessment ID not found', status: 'error' });
      return;
    }

    try {
      const { data } = await generateReportMutation.mutateAsync(assessmentId);
      const downloadUrl = data?.report_url;

      if (!downloadUrl) {
        toast({ title: 'Download URL not available', status: 'error' });
        return;
      }

      const link = document.createElement('a');
      Object.assign(link, {
        href: downloadUrl,
        download: '',
        style: 'display: none',
      });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      toast({
        title: 'Failed to download report',
        status: 'error',
        description: error?.message || 'Something went wrong. Try again.',
      });
    }
  };
  const integrityScores = {
    key: 'Integrity Score',
    value: integrity_score,
    tagProps: {
      bg:
        integrity_score?.toLowerCase() === 'acceptable'
          ? 'pastel-2-light'
          : integrity_score?.toLowerCase() === 'processing'
            ? 'pastel-6-light'
            : 'pastel-6-light',
      color:
        integrity_score?.toLowerCase() === 'acceptable'
          ? 'pastel-2-dark'
          : integrity_score?.toLowerCase() === 'processing'
            ? 'pastel-6-dark'
            : 'pastel-6-dark',
    },
  };
  const plagiarismScores = {
    key: 'Plagiarisms?',
    value: plagiarism_status,
    tagProps: {
      bg: plagiarism_status ? 'pastel-1-light' : 'pastel-2-light',
      color: plagiarism_status ? 'pastel-1-dark' : 'pastel-2-dark',
    },
  };

  return (
    <Flex flexDir="column" gap="12px">
      <Box p="20px" bg="secondary-25" borderRadius="sm">
        <Flex direction="column" gap="16px">
          <Flex direction="column" gap="4px">
            <KText color="secondary-900" size="sm-medium">
              Name of the test
            </KText>
            <KText>{test_name}</KText>
          </Flex>

          {completed_at && (
            <Flex direction="column" gap="4px">
              <KText color="secondary-900" size="sm-medium">
                Submission date & time
              </KText>
              <KText>
                {dayjs(completed_at).format(dateFormat.label)} {', '}
                {dayjs(completed_at).format(timeFormat)}
              </KText>
            </Flex>
          )}

          {(started_at || completed_at) && (
            <Flex gap="24px">
              {started_at && (
                <Flex direction="column" gap="4px">
                  <KText color="secondary-900" size="sm-medium">
                    Start time
                  </KText>
                  <KText>{dayjs(started_at).format(timeFormat)}</KText>
                </Flex>
              )}

              {completed_at && (
                <Flex direction="column" gap="4px">
                  <KText color="secondary-900" size="sm-medium">
                    End time
                  </KText>
                  <KText>{dayjs(completed_at).format(timeFormat)}</KText>
                </Flex>
              )}
              <Flex direction="column" gap="4px">
                <KText color="secondary-900" size="sm-medium">
                  Duration
                </KText>
                <KText>{calculatedDuration || 'N/A'}</KText>
              </Flex>
            </Flex>
          )}

          {score !== undefined &&
            score !== null &&
            max_score !== undefined &&
            max_score !== null &&
            !(score === 0 && max_score === 0) && (
              <Flex direction="column" gap="4px">
                <KText color="secondary-900" size="sm-medium">
                  Score
                </KText>
                <KText>
                  {score}/{max_score}
                </KText>
              </Flex>
            )}
          {platform === PLATFORMS.CRITERIACORP &&
            raw_score !== undefined &&
            raw_score !== null && (
              <Flex direction="column" gap="4px">
                <KText color="secondary-900" size="sm-medium">
                  Raw Score
                </KText>
                <KText>{raw_score}</KText>
              </Flex>
            )}
          {platform === PLATFORMS.CRITERIACORP &&
            percentile !== undefined &&
            percentile !== null && (
              <Flex direction="column" gap="4px">
                <KText color="secondary-900" size="sm-medium">
                  Percentile
                </KText>
                <KText>{percentile}</KText>
              </Flex>
            )}
          {platform === PLATFORMS.HACKERRANK && (
            <Flex direction="column" gap="4px">
              <KText color="secondary-900" size="sm-medium">
                Plagiarisms?
              </KText>
              <KTag
                bg={plagiarismScores.tagProps.bg}
                color={plagiarismScores.tagProps.color}
                width="fit-content"
                value={plagiarismScores.value ? 'Yes' : 'No'}
              />
            </Flex>
          )}

          {platform === PLATFORMS.HACKEREARTH && integrity_score && (
            <Flex direction="column" gap="4px">
              <KText color="secondary-900" size="sm-medium">
                Integrity Score
              </KText>
              <KTag
                bg={integrityScores.tagProps.bg}
                color={integrityScores.tagProps.color}
                width="fit-content"
                value={integrityScores.value}
              />
            </Flex>
          )}

          {(report_url || hireflix_url_private) && (
            <Link
              href={
                platform === PLATFORMS.HIREFLIX
                  ? hireflix_url_private
                  : report_url
              }
              target="_blank"
              rel="noopener noreferrer"
              w="100%"
              display="flex"
              alignItems="center"
              gap="6px"
            >
              <KText
                size="sm-medium"
                color="primary-600"
                _hover={{
                  textDecoration: 'underline',
                  color: 'primary-600',
                  cursor: 'pointer',
                }}
              >
                View full assessment and report
              </KText>
              {/* @ts-expect-error Icon as prop type mismatch with HeroIcons */}
              <Icon as={HiArrowTopRightOnSquare} color="primary-600" />
            </Link>
          )}
        </Flex>
      </Box>

      <Box mt="4">
        <Flex
          borderColor="border-color"
          borderWidth="1px"
          borderRadius="sm"
          p="12px 16px"
          alignItems="center"
          justifyContent="space-between"
          flexDir={isMobile ? 'column' : 'row'}
        >
          <KText
            size="md-semi-bold"
            color="secondary-900"
            mb={isMobile ? '12px' : '0'}
          >
            Assessment Submission
          </KText>
          <Flex gap="6px">
            {platform !== PLATFORMS.HIREFLIX &&
              platform !== PLATFORMS.CODEINTERVIEW && (
                <Tooltip
                  label={
                    !pdf_url
                      ? platform === PLATFORMS.HACKEREARTH
                        ? 'HackerEarth assessment reports typically take around 30 minutes to be generated'
                        : 'Assessment report is not available'
                      : ''
                  }
                  hasArrow
                  isDisabled={Boolean(pdf_url)}
                >
                  <Box>
                    <KButton
                      variant="quaternary"
                      size="sm"
                      icon={Eye}
                      p="6px 12px !important"
                      onClick={handleView}
                      isDisabled={!pdf_url}
                    >
                      View
                    </KButton>
                  </Box>
                </Tooltip>
              )}
            {platform === PLATFORMS.CODEINTERVIEW ? (
              <KButton
                variant="quaternary"
                size="sm"
                icon={FiExternalLink}
                p="6px 12px !important"
                onClick={() => {
                  window.open(
                    `${CODEINTERVIEW_RESULT_BASE_URL}${assessmentDetails?.ext_test_id}&report=true`,
                    '_blank'
                  );
                }}
                isDisabled={!assessmentDetails?.ext_test_id}
              >
                View result
              </KButton>
            ) : (
              <KButton
                variant="quaternary"
                size="sm"
                icon={copied ? Check : Copy03}
                p="6px 12px !important"
                onClick={handleCopy}
                isDisabled={!report_url}
              >
                {copied ? 'Copied' : 'Copy link'}
              </KButton>
            )}
            {platform !== PLATFORMS.CRITERIACORP &&
              platform !== PLATFORMS.HIREFLIX &&
              platform !== PLATFORMS.CODEINTERVIEW && (
                <Tooltip
                  label={
                    !isPlatformEnabled
                      ? platformConfig.reconnectMessage
                      : !pdf_url
                        ? platform === PLATFORMS.HACKEREARTH
                          ? 'HackerEarth assessment reports typically take around 30 minutes to be generated'
                          : 'Assessment report is not available'
                        : ''
                  }
                  hasArrow
                  isDisabled={Boolean(isPlatformEnabled && pdf_url)}
                >
                  <Box>
                    <KButton
                      variant="quaternary"
                      size="sm"
                      icon={Download02}
                      p="6px 12px !important"
                      onClick={handleDownload}
                      isLoading={generateReportMutation.isPending}
                      isDisabled={
                        !isPlatformEnabled ||
                        !pdf_url ||
                        generateReportMutation.isPending
                      }
                    >
                      Download
                    </KButton>
                  </Box>
                </Tooltip>
              )}
          </Flex>
        </Flex>
      </Box>

      <PreviewPdfModal
        isOpen={isPdfModalOpen}
        onClose={() => {
          setIsPdfModalOpen(false);
          if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl(null);
          }
        }}
        file={{
          url: pdfBlobUrl || pdf_url,
          file_name: test_name || 'Assessment Report',
          content_type: 'application/pdf',
        }}
      />
    </Flex>
  );
};
